import { createStore } from "./hooks.js";
import { cmd, dispatch, uuid } from "./sync.js";

/**
 * Pomodoro and stopwatch timer. State lives here (and in localStorage) so it keeps
 * running while you move around the app or reload the page.
 */
export type Phase = "focus" | "short" | "long";
export interface FocusSettings { focusMin: number; shortMin: number; longMin: number; longEvery: number; autoStart: boolean }
export interface FocusState {
  mode: "pomodoro" | "stopwatch";
  phase: Phase;
  taskId: string | null;
  running: boolean;
  /** epoch ms when the current run started (null when paused/idle) */
  startedAt: number | null;
  /** ms already accumulated before the current run */
  elapsedMs: number;
  /** epoch ms when this focus block first started, for the session record */
  blockStart: number | null;
  completedFocus: number;
  settings: FocusSettings;
}

const KEY = "todoer.focus";
const defaults: FocusState = {
  mode: "pomodoro", phase: "focus", taskId: null, running: false, startedAt: null, elapsedMs: 0, blockStart: null, completedFocus: 0,
  settings: { focusMin: 25, shortMin: 5, longMin: 15, longEvery: 4, autoStart: false },
};

function load(): FocusState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...defaults, ...JSON.parse(raw), settings: { ...defaults.settings, ...JSON.parse(raw).settings } };
  } catch { /* ignore */ }
  return defaults;
}

export const focus = createStore<FocusState>(load());
const save = () => { try { localStorage.setItem(KEY, JSON.stringify(focus.get())); } catch { /* ignore */ } };
export const useFocus = () => focus.use();

export const phaseMinutes = (s: FocusState) =>
  s.phase === "focus" ? s.settings.focusMin : s.phase === "short" ? s.settings.shortMin : s.settings.longMin;

export function elapsedMs(s: FocusState, now = Date.now()): number {
  return s.elapsedMs + (s.running && s.startedAt ? now - s.startedAt : 0);
}

export function remainingMs(s: FocusState, now = Date.now()): number {
  return Math.max(0, phaseMinutes(s) * 60_000 - elapsedMs(s, now));
}

function update(patch: Partial<FocusState> | ((s: FocusState) => Partial<FocusState>)) {
  focus.set((s) => ({ ...s, ...(typeof patch === "function" ? patch(s) : patch) }));
  save();
}

export function start(taskId?: string | null): void {
  update((s) => ({
    running: true, startedAt: Date.now(), taskId: taskId !== undefined ? taskId : s.taskId,
    blockStart: s.blockStart ?? (s.phase === "focus" || s.mode === "stopwatch" ? Date.now() : null),
  }));
}
export function pause(): void {
  update((s) => ({ running: false, startedAt: null, elapsedMs: elapsedMs(s) }));
}
export function setTask(taskId: string | null): void { update({ taskId }); }
export function setMode(mode: FocusState["mode"]): void { reset(); update({ mode, phase: "focus" }); }
export function setSettings(settings: Partial<FocusSettings>): void { update((s) => ({ settings: { ...s.settings, ...settings } })); }

export function reset(): void {
  update({ running: false, startedAt: null, elapsedMs: 0, blockStart: null });
}

/** Records the focus time so far (if any) as a session. */
async function record(s: FocusState): Promise<void> {
  const ms = elapsedMs(s);
  if (ms < 60_000 || !s.blockStart) return;
  const end = Date.now();
  await dispatch(cmd("focus_add", {
    id: uuid(), taskId: s.taskId, kind: s.mode, startedAt: new Date(s.blockStart).toISOString(), endedAt: new Date(end).toISOString(),
    durationSec: Math.round(ms / 1000),
  }));
}

/** Stops the stopwatch or abandons a pomodoro early, saving any focus time over a minute. */
export async function stop(): Promise<void> {
  const s = focus.get();
  if (s.mode === "stopwatch" || s.phase === "focus") await record(s);
  update({ running: false, startedAt: null, elapsedMs: 0, blockStart: null, phase: "focus" });
}

/** Moves to the next phase (focus → break → focus). Called when a phase ends or is skipped. */
export async function nextPhase(completed: boolean): Promise<void> {
  const s = focus.get();
  if (s.phase === "focus") {
    if (completed) await record(s);
    const done = s.completedFocus + (completed ? 1 : 0);
    const long = done > 0 && done % s.settings.longEvery === 0;
    update({ phase: long ? "long" : "short", completedFocus: done, elapsedMs: 0, startedAt: s.settings.autoStart ? Date.now() : null, running: s.settings.autoStart, blockStart: null });
  } else {
    update({ phase: "focus", elapsedMs: 0, startedAt: s.settings.autoStart ? Date.now() : null, running: s.settings.autoStart, blockStart: s.settings.autoStart ? Date.now() : null });
  }
}

/** A soft three-note chime using Web Audio. */
export function chime(): void {
  try {
    const ctx = new AudioContext();
    [660, 880, 990].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.18);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + i * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.18 + 0.5);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.18);
      o.stop(ctx.currentTime + i * 0.18 + 0.55);
    });
  } catch { /* audio unavailable */ }
}

let ticking = false;
/** Starts the global ticker that ends phases on time, even when the Focus page is closed. */
export function startFocusTicker(): void {
  if (ticking) return;
  ticking = true;
  setInterval(() => {
    const s = focus.get();
    if (!s.running || s.mode !== "pomodoro") return;
    if (remainingMs(s) <= 0) {
      chime();
      const msg = s.phase === "focus" ? "Focus session done. Time for a break." : "Break over. Ready to focus?";
      try { if (Notification.permission === "granted") new Notification("Todoer focus", { body: msg, icon: "/icons/icon-192.png" }); } catch { /* ignore */ }
      void nextPhase(true);
    }
  }, 1000);
}

export const fmtClock = (ms: number) => {
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h ? `${h}:` : ""}${String(m).padStart(h ? 2 : 1, "0")}:${String(s).padStart(2, "0")}`;
};
